import { CheckIcon } from '@heroicons/react/24/outline';
import { useCallback, useMemo } from 'react';

import { Tooltip } from '@/components/Tooltip';
import { Container } from '@/components/legacy-ui';
import { cn } from '@/utils/classname';

import {
  AttackSurfaceOption,
  AttackSurfaceSelection,
  AttackSurfaceSelectorProps,
} from './AttackSurfaceSelector.types';
import {
  DEFAULT_ATTACK_SURFACE_OPTIONS,
  DEFAULT_REQUIRED_OPTIONS,
} from './constants';

export function AttackSurfaceSelector({
  value,
  onChange,
  options = DEFAULT_ATTACK_SURFACE_OPTIONS,
  disabledOptions = [],
  requiredOptions = DEFAULT_REQUIRED_OPTIONS,
  onSelectionChange,
  className,
  variant = 'default',
  showLabels = true,
  showDescriptions = false,
  showCheckIcon = false,
}: AttackSurfaceSelectorProps) {
  const handleSelectionChange = useCallback(
    (optionValue: keyof AttackSurfaceSelection) => {
      // Prevent deselecting required options
      if (requiredOptions.includes(optionValue) && value[optionValue]) {
        return;
      }

      // Prevent selecting disabled options
      if (disabledOptions.includes(optionValue) && !value[optionValue]) {
        return;
      }

      const newValue = {
        ...value,
        [optionValue]: !value[optionValue],
      };

      // Call the onChange handler
      onChange(newValue);

      // Call the optional selection change handler for side effects
      if (onSelectionChange) {
        onSelectionChange(optionValue, newValue[optionValue]);
      }
    },
    [value, onChange, onSelectionChange, requiredOptions, disabledOptions]
  );

  const containerClassName = useMemo(() => {
    switch (variant) {
      case 'compact':
        return 'flex items-center gap-2 py-1.5 px-3 text-sm';
      case 'list':
        return showDescriptions
          ? 'flex items-start gap-3 py-3 px-4 mb-2'
          : 'flex items-center gap-3 py-3 px-4 mb-2';
      default:
        return 'flex w-fit items-center gap-3 py-2';
    }
  }, [variant, showDescriptions]);

  const renderOption = (option: AttackSurfaceOption) => {
    const isSelected = value[option.value];
    const isRequired = requiredOptions.includes(option.value);
    const isDisabled = disabledOptions.includes(option.value);
    const isClickable = !(isRequired && isSelected) && !isDisabled;

    const optionContent = (
      <Container
        key={option.value}
        onClick={
          isClickable ? () => handleSelectionChange(option.value) : undefined
        }
        className={cn(
          containerClassName,
          !isClickable && 'cursor-not-allowed opacity-60',
          isClickable && 'cursor-pointer'
        )}
        isSelected={isSelected}
        data-testid={`attack-surface-${option.value}`}
      >
        <div className="flex flex-1 items-center justify-between">
          {showLabels && (
            <div className="flex flex-1 flex-col gap-1">
              <div className="flex flex-col">
                <span className="font-medium">{option.label}</span>
                {showDescriptions && option.description && (
                  <span className="text-chariot-text-secondary text-sm">
                    {option.description}
                  </span>
                )}
              </div>
            </div>
          )}
          {showCheckIcon && isSelected && (
            <CheckIcon className="mr-3 h-6 w-6 flex-shrink-0 text-green-500" />
          )}
        </div>
      </Container>
    );

    // Wrap in tooltip if tooltip text is provided
    if (option.tooltip && !showLabels) {
      return (
        <Tooltip key={option.value} title={option.tooltip}>
          {optionContent}
        </Tooltip>
      );
    }

    return optionContent;
  };

  const wrapperClassName = useMemo(() => {
    switch (variant) {
      case 'list':
        return 'flex flex-col w-full';
      case 'compact':
        return 'flex flex-wrap gap-2';
      default:
        return 'flex gap-3';
    }
  }, [variant]);

  return (
    <div
      className={cn(wrapperClassName, className)}
      role="group"
      aria-label="Attack Surface Selection"
    >
      {options.map(renderOption)}
    </div>
  );
}

// Export types for external use
export type {
  AttackSurfaceOption,
  AttackSurfaceSelection,
  AttackSurfaceSelectorProps,
} from './AttackSurfaceSelector.types';
/* eslint-disable react-refresh/only-export-components -- Re-exporting constants for convenience */
export {
  DEFAULT_ATTACK_SURFACE_OPTIONS,
  DEFAULT_REQUIRED_OPTIONS,
} from './constants';
/* eslint-enable react-refresh/only-export-components */
